import { useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export function SettingCard({ title, body, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-border bg-card p-5 sm:p-6">
      <h3 className="font-serif text-xl font-semibold text-foreground">{title}</h3>
      {body && <p className="mt-1 text-sm text-muted-foreground">{body}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function SaveButton({ busy, children, testId, variant }) {
  return (
    <Button type="submit" disabled={busy} data-testid={testId} variant={variant} className={variant ? "rounded-full" : "rounded-full bg-primary text-primary-foreground hover:bg-primary/90"}>
      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {children}
    </Button>
  );
}

export function useSave(method, url, onDone) {
  const [busy, setBusy] = useState(false);
  const run = async (payload, okMessage) => {
    setBusy(true);
    try {
      const { data } = await api[method](url, payload);
      toast.success(okMessage);
      onDone?.(data);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

export function NameCard({ initial, onSaved }) {
  const [name, setName] = useState(initial || "");
  const [busy, save] = useSave("put", "/account/name", onSaved);
  return (
    <SettingCard title="Your name" body="Shown on your journal, highlights and blog author card." testId="account-name-card">
      <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={(e) => { e.preventDefault(); save({ name }, "Name updated."); }}>
        <div className="flex-1"><Field label="Display name"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required data-testid="account-name-input" /></Field></div>
        <SaveButton busy={busy} testId="account-name-save">Save name</SaveButton>
      </form>
    </SettingCard>
  );
}
